import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { GetAllBookingResponseV4Schema } from "../schema/GetAllBookingResponseV4";

const API_VERSION = "v4";

export interface DateProp {
    gte: string;
    lte: string;
}

interface FilterProps {
    all?: number;
    bookingId?: string;
    status?: string;
    mark?: string;
    source?: string;
    payment_type?: string;
    room_type_id?: string;
    room_id?: string | null;
    rate_plan_id?: string;
    invoice_number?: string;
    voucher_number?: string;
    ota_id?: string;
    checkin_date?: DateProp;
    checkout_date?: DateProp;
    created_at?: DateProp;
    invoice_date?: DateProp;
    property_id?: string;
    agent_id?: string;
}

interface GetAllBookingProps {
    filters?: FilterProps,
    search?: string,
    limit?: number,
    queryKey?: string,
    orderBy?: string,
    sort?: string,
    page?: number,
    include?: string,
}

const useGetAllBookingV4 = (query: GetAllBookingProps) => {
    const cleanQueryKey = (query: GetAllBookingProps) => [
        "bookings-v4",
        "booking-v4",
        query.filters?.all ? "all" : "",
        ...Object.entries(query.filters ?? {})
            .filter(([key, value]) => key !== "all" && value != null && value !== "")
            .map(([key, value]) =>
                typeof value === "object" ? `${key}:${JSON.stringify(value)}` : `${key}:${value}`
            ),
        query.search,
        query.queryKey,
        query.orderBy,
        query.sort,
        query.page,
    ].filter(Boolean);

    return useQuery({
        queryKey: cleanQueryKey(query),
        queryFn: async () => {
            try {
                const params = {
                    filter: Object.fromEntries(
                        Object.entries(query.filters ?? {}).filter(([key, value]) => key !== "all" && value !== undefined && value !== null && value !== "")
                    ),
                    // Adjusted include fields for v4
                    include: query.include || "property,room_type,room,rate_plan",
                    search: query.search || "",
                    limit: query.limit ?? 10,
                    orderBy: query.orderBy ?? "created_at",
                    sort: query.sort ?? "desc",
                    page: query.page ?? 1,
                    ...(query.filters?.all ? { all: query.filters.all } : {}),
                };

                const response = await privateApi.get(`/${API_VERSION}/booking`, { params });

                // Validasi response
                const validationResult = GetAllBookingResponseV4Schema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed V4:", validationResult.error.errors);
                    throw new Error("Invalid Bookings data format V4");
                }
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch Bookings V4`, error);
                throw error;
            }
        },
        retry: import.meta.env.VITE_MAX_RETRY,
        refetchOnWindowFocus: false,
    });
};

export default useGetAllBookingV4;
